import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LogOut, Clock, AlertTriangle, CheckCircle, FileText } from "lucide-react";
import ProtectedRoute from "./ProtectedRoute";
import SidebarNavigation from "./navigation/SidebarNavigation";

// Import the modules
import UserManagement from "./modules/UserManagement";
import SupplierManagement from "./modules/SupplierManagement";
import PriceDatabase from "./modules/PriceDatabase";
// Home module removed
import PricingStandard from "./modules/PricingStandard";
import CostModelModule from "./modules/CostModel";
import BillOfQuantitiesModule from "./modules/BillOfQuantities";
import UpdateSchedule from "./modules/UpdateSchedule";
import HistoricalPrices from "./modules/HistoricalPrices";
import RegionalFactors from "./modules/RegionalFactors";
import PriceTrends from "./modules/PriceTrends";
import LaborDatabase from "./modules/pricing/LaborDatabase";
import RateCalculator from "./modules/pricing/RateCalculator";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [activeModule, setActiveModule] = useState("pricing-standard");

  // Debug logging
  console.log("Current user:", user);
  console.log("User permissions:", user?.permissions);

  const procurementData = [
    { month: "Jan", amount: 2.4, contracts: 45 },
    { month: "Feb", amount: 1.8, contracts: 32 },
    { month: "Mar", amount: 3.2, contracts: 58 },
    { month: "Apr", amount: 2.9, contracts: 49 },
    { month: "May", amount: 4.1, contracts: 67 },
    { month: "Jun", amount: 3.7, contracts: 54 }
  ];

  const sectorData = [
    { name: "Health", value: 35, color: "#0F7D37" },
    { name: "Education", value: 25, color: "#FFD700" },
    { name: "Infrastructure", value: 20, color: "#2563EB" },
    { name: "Defense", value: 12, color: "#DC2626" },
    { name: "Others", value: 8, color: "#6B7280" }
  ];

  const recentTenders = [
    { id: "TND-2024-001", title: "Medical Equipment Supply", ministry: "Health", status: "active", deadline: "2024-01-15", bids: 23 },
    { id: "TND-2024-002", title: "School Infrastructure Development", ministry: "Education", status: "evaluation", deadline: "2024-01-10", bids: 15 },
    { id: "TND-2024-003", title: "IT Equipment Procurement", ministry: "Communications", status: "awarded", deadline: "2024-01-05", bids: 31 },
    { id: "TND-2024-004", title: "Road Construction Project", ministry: "Works", status: "active", deadline: "2024-01-20", bids: 8 }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-500";
      case "evaluation": return "bg-yellow-500";
      case "awarded": return "bg-blue-500";
      default: return "bg-gray-500";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active": return <Clock className="h-3 w-3" />;
      case "evaluation": return <AlertTriangle className="h-3 w-3" />;
      case "awarded": return <CheckCircle className="h-3 w-3" />;
      default: return <FileText className="h-3 w-3" />;
    }
  };

  const handleLogout = () => {
    logout();
  };

  const renderModule = () => {
    switch (activeModule) {
      // 'home' module removed
      case "users":
        return (
          <ProtectedRoute requiredPermission="manage_users">
            <UserManagement />
          </ProtectedRoute>
        );
      case "suppliers":
        return (
          <ProtectedRoute requiredPermission="manage_suppliers">
            <SupplierManagement />
          </ProtectedRoute>
        );
      case "prices":
        return <PriceDatabase onNavigate={setActiveModule} />;
      case "pricing-standard":
        return <PricingStandard />;
      case "labour-database":
        return <LaborDatabase />;
      case "rate-calculator":
        return <RateCalculator pricingStandards={[]} />;
      case "cost-model":
        return <CostModelModule key="cost-model" initialTab="models" />;
      case "cost-model-overview":
        return <CostModelModule key="cost-model-overview" initialTab="overview" />;
      case "cost-model-components":
        return <CostModelModule key="cost-model-components" initialTab="components" />;
      case "cost-model-subcomponents":
        return <CostModelModule key="cost-model-subcomponents" initialTab="subcomponents" />;
      case "cost-model-geozones":
        return <CostModelModule key="cost-model-geozones" initialTab="geozones" />;
      case "cost-model-models":
        return <CostModelModule key="cost-model-models" initialTab="models" />;
      case "bill-of-quantities":
        return <BillOfQuantitiesModule />;
      case "price-schedule":
        return (
          <ProtectedRoute requiredPermission="view_all_tenders">
            <UpdateSchedule />
          </ProtectedRoute>
        );
      case "historical-prices":
        return (
          <ProtectedRoute requiredPermission="view_all_tenders">
            <HistoricalPrices />
          </ProtectedRoute>
        );
      case "regional-factors":
        return (
          <ProtectedRoute requiredPermission="view_all_tenders">
            <RegionalFactors />
          </ProtectedRoute>
        );
      case "price-trends":
        return (
          <ProtectedRoute requiredPermission="view_all_tenders">
            <PriceTrends />
          </ProtectedRoute>
        );
      default:
        return <PricingStandard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://www.bpp.gov.ng/wp-content/uploads/2019/02/BPP-LOGO-2-1.png" 
                alt="BPP Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Nigeria National Procurement Platform</h1>
                <p className="text-sm text-gray-600">Bureau of Public Procurement - Secure Access</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium">Welcome, {user?.username}</div>
                <Badge variant="secondary" className="capitalize">
                  {user?.role?.replace('_', ' ')} User
                </Badge>
              </div>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Secure Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <SidebarNavigation 
          activeModule={activeModule} 
          onModuleChange={setActiveModule} 
        />

        {/* Main Content */}
        <div className="flex-1 p-6">
          {renderModule()}
        </div>
      </div>
    </div>
  );

  // dashboard view removed
};

export default Dashboard;
